/**
 * Types for the staff merchant-onboarding feature.
 *
 * These mirror the REAL backend contract (Java source = source of truth),
 * NOT the generated OpenAPI schema — `/api/v1/staff/**` and the
 * `PENDING_OWNER_ACTIVATION` status / `DispatchMode` enum are not yet
 * emitted in `src/api/generated/api.d.ts` (the staff-onboarding endpoints
 * post-date the last regen). We hand-author them here and drop the locals
 * once the OpenAPI catches up — same approach as `features/merchants` and
 * `features/registrations`.
 *
 * Contract refs (BackendFavelaFood):
 *  - `staffonboarding/dto/StaffCreateMerchantRequest.java`
 *  - `staffonboarding/dto/StaffCreateMerchantResponse.java`
 *  - `staffonboarding/dto/StaffMerchantResponse.java`
 *  - `staffonboarding/dto/ResendInviteResponse.java`
 *  - `merchant/entity/{BusinessTypeEnum,DispatchMode,MerchantStatusEnum}.java`
 *  - `staffonboarding/entity/InviteDeliveryStatus.java`
 */

import type { PixKeyType } from '../../lib/pix';

/* ── Enums (mirror Java; generated schema is stale) ────────────────── */

export const BUSINESS_TYPE_VALUES = [
  'RESTAURANT',
  'MARKET',
  'PHARMACY',
  'PET_SHOP',
  'CONVENIENCE',
  'OTHER',
] as const;
export type BusinessType = (typeof BUSINESS_TYPE_VALUES)[number];

export const DISPATCH_MODE_VALUES = ['OWN_COURIERS', 'GLOBAL_POOL'] as const;
export type DispatchMode = (typeof DISPATCH_MODE_VALUES)[number];

export const MERCHANT_STATUS_VALUES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'SUSPENDED',
  'PENDING_OWNER_ACTIVATION',
] as const;
export type MerchantStatus = (typeof MERCHANT_STATUS_VALUES)[number];

export const INVITE_DELIVERY_STATUS_VALUES = [
  'PENDING',
  'EMAIL_SENT',
  'SMS_SENT',
  'BOTH_SENT',
  'FAILED',
] as const;
export type InviteDeliveryStatus = (typeof INVITE_DELIVERY_STATUS_VALUES)[number];

/** ISO-8601 day of week (1 = Monday … 7 = Sunday) — matches the DB CHECK. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type IsoWeekday = (typeof ISO_WEEKDAYS)[number];

/* ── Request payloads (sent to the backend) ────────────────────────── */

/** Inline address — mirrors `merchant/dto/MerchantAddressInline.java`. */
export interface MerchantAddressPayload {
  /** 8 digits, no mask (backend `@Pattern("\\d{8}")`). */
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  /** 2-letter UF, uppercase. */
  state: string;
  /**
   * Optional manual map pin (REQ-delivery-radius-geolocation RF-004). Both-or-neither: the backend
   * (`MerchantAddressInline.@AssertTrue`) rejects a partial pair. Decimal degrees, WGS84.
   * TODO: regen api.d.ts via `make admin-gen-api` once the backend OpenAPI emits these fields.
   */
  latitude?: number;
  longitude?: number;
}

/** One row of `operatingHours` — mirrors `OperatingHoursRequest.java`. */
export interface OperatingHoursPayload {
  /** ISO 1..7. */
  dayOfWeek: IsoWeekday;
  /** `HH:mm` (Jackson `LocalTime`). */
  openTime: string;
  /** `HH:mm`. */
  closeTime: string;
  isClosed: boolean;
}

/**
 * Body for `POST /api/v1/staff/merchants` (`StaffCreateMerchantRequest`).
 *
 * `tempPassword` (FAV-112 hybrid) is in the DTO: staff sets a temporary
 * password (Keycloak TEMPORARY → owner changes it on first login). Owner
 * document photos (FAV-142) are in the DTO too — with either present the
 * owner is created `documentStatus = PENDING` (fila de aprovação).
 */
export interface StaffCreateMerchantPayload {
  // Step 1 — establishment
  tradeName: string;
  legalName?: string;
  /** Digits only (14). */
  cnpj: string;
  businessType: BusinessType;
  categoryId?: string;
  /** Legacy free-text category (backend `Merchant.category` is NOT NULL). */
  category: string;
  description?: string;
  phone?: string;
  // Step 2 — responsible (owner)
  ownerFullName: string;
  /** Digits only (11). */
  ownerCpf: string;
  ownerPhone: string;
  ownerEmail: string;
  /** FAV-112 hybrid — Keycloak TEMPORARY password, owner changes on first login. */
  tempPassword: string;
  // Step 3 — address
  address: MerchantAddressPayload;
  // Step 4 — media
  logoUrl: string;
  /** Owner document photos (FAV-142) — either present ⇒ owner documentStatus=PENDING. */
  ownerFacePhotoUrl?: string;
  ownerIdentityPhotoUrl?: string;
  // Step 5 — operations
  operatingHours: OperatingHoursPayload[];
  minimumOrderAmount?: number;
  estimatedPrepMinutes?: number;
  dispatchMode?: DispatchMode;
  autoAcceptOrders?: boolean;
  // Step 4 — PIX payout key (FAV-172). Optional; (key, type) validated together
  // by the backend `@ValidPixKey`. Omitted entirely when no key is entered.
  pixKey?: string;
  pixKeyType?: PixKeyType;
}

/* ── Response shapes (received from the backend) ───────────────────── */

/** `StaffMerchantResponse.java`. */
export interface StaffMerchantResponse {
  merchantId: string;
  tradeName: string;
  status: MerchantStatus;
  cnpjMasked: string;
  logoUrl?: string | null;
  inviteSentAt?: string | null;
  inviteExpiresAt?: string | null;
  inviteAccepted: boolean;
  inviteDeliveryStatus: InviteDeliveryStatus;
  createdAt?: string | null;
}

/** Masked recipient block of `StaffCreateMerchantResponse.InviteRecipient`. */
export interface InviteRecipient {
  emailMasked?: string | null;
  phoneMasked?: string | null;
}

/** `StaffCreateMerchantResponse.java`. */
export interface StaffCreateMerchantResponse {
  merchant: StaffMerchantResponse;
  inviteSentTo: InviteRecipient;
  /** Link copiável de ativação (decisão 2026-07-07); null no caminho linked-owner (FAV-156). */
  inviteUrl?: string | null;
}

/** `ResendInviteResponse.java`. */
export interface ResendInviteResponse {
  inviteExpiresAt?: string | null;
  resendCount: number;
  inviteDeliveryStatus: InviteDeliveryStatus;
  /** Link copiável do token recém-emitido (decisão 2026-07-07). */
  inviteUrl?: string | null;
}

/**
 * Body for `PATCH /api/v1/staff/merchants/{id}` (`StaffUpdateMerchantRequest`).
 * All fields optional; only non-null ones are applied. Owner identity, CPF,
 * e-mail and CNPJ are immutable through this path (omitted by design).
 */
export interface StaffUpdateMerchantPayload {
  tradeName?: string;
  legalName?: string;
  businessType?: BusinessType;
  categoryId?: string;
  category?: string;
  description?: string;
  phone?: string;
  logoUrl?: string;
  minimumOrderAmount?: number;
  estimatedPrepMinutes?: number;
  dispatchMode?: DispatchMode;
  autoAcceptOrders?: boolean;
}
